import { Component, DestroyRef, inject } from '@angular/core';
import { FormMode } from '../../../shared/types/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TitledInputComponent } from '../../../shared/components/titled-input/titled-input.component';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { ManagementService } from '../management.service';
import { CustomValidators } from '../../../shared/validators';
import { objectArrayToSelectOptions, stringArrayToSelectOptions } from '../../../utils/mappings';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {AccountManagementColumn, BrandManagementColumn, UserPayload} from '../types';
import { StateService } from '../../../services/state.service';
import { seperatedStringToArray } from '../../../utils/strings';
import { UtilService } from '../../../services/util.service';
import {
  SelectItem,
  SelectMenuComponent,
  SelectMode
} from '../../../shared/components/select-menu/select-menu.component';
import {NgClass, NgIf} from '@angular/common';
import {CommunicationService} from '../../../http/communication.service';

@Component({
  selector: 'app-user-modal',
  imports: [ReactiveFormsModule, TitledInputComponent, ButtonComponent, SelectMenuComponent, NgClass, NgIf],
  templateUrl: './user-modal.component.html',
  styleUrl: './user-modal.component.scss'
})
export class UserModalComponent {
  public currentMode: FormMode = FormMode.ADD;
  form: FormGroup<{
    email: FormControl<string | null>;
    firstName: FormControl<string | null>;
    lastName: FormControl<string | null>;
    role: FormControl<string | null>;
    accountKey: FormControl<string | null>
  }>;
  public configs = {
    [FormMode.ADD]:{
      title: 'Add user',
      submitText: 'Add',
      submitFunction: ()=> this.addUser()
    },
    [FormMode.EDIT]:{
      title: 'Edit user',
      submitText: 'Update',
      submitFunction: ()=> this.editUser()
    }
  };
  public rolesOptions: SelectItem[] = [];
  public accountOptions: SelectItem[] = [];
  public data = inject(MAT_DIALOG_DATA);
  public isVerified = !!this.data?.verifiedEmail;
  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);

  constructor(private dialogRef: MatDialogRef<UserModalComponent>, private managementService: ManagementService,
    private communicationService: CommunicationService, private stateService: StateService, private utilService: UtilService){
    this.form = this.fb.group({
      email: new FormControl({ value: '', disabled: !!this.data },[CustomValidators.required('Email'),CustomValidators.email('Email')]),
      firstName: new FormControl('', [CustomValidators.required('First Name')]),
      lastName: new FormControl('', [CustomValidators.required('Last Name')]),
      role: new FormControl('', [CustomValidators.required('Role')]),
      accountKey: new FormControl<string>('')
    });
    if (this.data) {
      this.currentMode = FormMode.EDIT;
      this.form.patchValue(({
        ...this.data,
        accountKey: this.data.account ? this.data.account.key : undefined,
      }));
    }
    this.accountOptions = objectArrayToSelectOptions(this.managementService.accountList, AccountManagementColumn.ACCOUNT_KEY, AccountManagementColumn.NAME);
    this.rolesOptions = stringArrayToSelectOptions(this.managementService.userRoles);
  }

  get email() {
    return this.form.get('email') as FormControl;
  }

  get firstName() {
    return this.form.get('firstName') as FormControl;
  }

  get lastName() {
    return this.form.get('lastName') as FormControl;
  }

  get role() {
    return this.form.get('role') as FormControl;
  }

  get accountKey() {
    return this.form.get('accountKey') as FormControl;
  }

  submit(){
    this.stateService.loading = true;
    this.configs[this.currentMode].submitFunction();
  }

  cancel(){
    this.dialogRef.close();
  }

  getUserPayload(){
    return {
      ...this.form.getRawValue(),
      id: this.data?.id
    } as UserPayload
  }

  addUser(){
    this.managementService.addUser(this.getUserPayload()).pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: result => {
        this.stateService.updateAccountsOnUserUpdate(result['account'], result.email as string)
        this.dialogRef.close(result)
      },
      error: error => {
        this.stateService.loading = false;
        this.utilService.openSnackBar(error.error.message, {variant: 'error', position: 'center'})
      }
    })
  }

  editUser(){
    this.managementService.editUser(this.getUserPayload()).pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: result => { this.dialogRef.close(result)},
      error: error => {
        this.stateService.loading = false;
        this.utilService.openSnackBar(error.error.message, {variant: 'error', position: 'center'})
      }
    })
  }

  resendInvite(){
    this.communicationService.httpPost('/users/send-password-reset-email', {email: this.data?.email} )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.utilService.openSnackBar('Invite sent successfully', {variant: 'success', position: 'center', duration: 3000}),
        error: () => this.utilService.openSnackBar('Failed to send Invite', {variant: 'error', position: 'center', duration: 3000})
      });
  }

  protected readonly SelectMode = SelectMode;
  protected readonly FormMode = FormMode;
}
